import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  CheckCircle2, ChevronRight, Sparkles, FileDown, ArrowRight,
  Network, Cpu, Database,
  Route as RouteIcon, Radio, Droplets, Hammer, Shield, Building2, Leaf,
  Eye, AlertTriangle, BarChart3, Workflow, Lock,
} from "lucide-react";
import usePageview, { getVisitorSession } from "@/lib/usePageview";
import useNoIndex from "@/lib/useNoIndex";
import BespokePageRibbon from "@/components/BespokePageRibbon";
import BespokeWatermark from "@/components/BespokeWatermark";
import VideoAd from "@/components/VideoAd";

const VIDEO_EXEC = "https://customer-assets.emergentagent.com/job_analytics-vault-5/artifacts/3fzn18bh_output.mp4";

// Phase 25.13am — Ventia bespoke proposal landing page.
//
// Built for a specific prospect conversation. Public URL (no auth gate) so
// the prospect can share with internal stakeholders without account setup.
// Surfaces: 7 sector tiles mapped to DARA modules, the architectural
// "DARA-between-asset-and-Maximo" diagram, 3 commercial models, and a
// scoped lead-capture form persisting to db.industry_leads under
// `industry: "ventia"`.
//
// Logo-free by design: we use a "VENTIA × DARA OS" text mark to avoid
// trademark issues and to make it feel like a bespoke proposal instead of
// a templated marketing page.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// 7 service sectors, each mapped to DARA capability + business case + roadmap honesty.
// Phase 25.16f — `demo_profile` field maps each sector card to the matching
// SITE_PROFILES key on /industrial-demo so reviewers can click straight from
// "sector card on the proposal" into "live sector demo with bespoke narrator".
const SECTORS = [
  {
    Icon: RouteIcon,
    code: "01",
    demo_profile: "transport",
    name: "Transport · Roads · Tunnels · Motorways",
    mvp: "OPC UA + MQTT connector on top of tunnel SCADA + motorway-management systems. Ventilation fan, lighting circuit, VMS sign, CCTV-health anomaly briefs. Read-only. No PLC writes.",
    roadmap: "TMR Queensland · TfNSW · VicRoads asset-owner reporting integration. Pavement-condition predictive scoring.",
    proof: "One avoided tunnel-closure event = $200k-$1M penalty avoidance + reputational headroom.",
  },
  {
    Icon: Radio,
    code: "02",
    demo_profile: "telco",
    name: "Telecommunications · Chorus · NBN · Transpower",
    mvp: "Read-only ingest of NMS feeds (Cisco / Nokia / Ericsson alarm stacks). Cross-carrier fault correlation — one alert when NBN + Chorus + Transpower hit the same site. Backhaul utilisation drift detection.",
    roadmap: "Direct ingest from NBN's NMS B2B exposure. Drone-inspection image classifier for tower health.",
    proof: "One missed P1 outage = $50-150k SLA + extended-fault penalty per event.",
  },
  {
    Icon: Droplets,
    code: "03",
    demo_profile: "water",
    name: "Water Utilities · Treatment · Reticulation",
    mvp: "OSIsoft PI / Wonderware / Citect read-only ingest. ISO 10816 pump-degradation curves, motor-current-signature analysis. Tuesday operational brief — UV-dose & residual-chlorine trending. Bushfire / flood cross-reference when env-services connected.",
    roadmap: "DOC / turbidity forecasting. Pre-baked 2027 regulator pathway (SA Water digital twin, Water Corp WA reporting).",
    proof: "1 avoided pump failure at a major plant = $80-300k. 1 avoided UV-dose breach = avoided licence-incident.",
  },
  {
    Icon: Hammer,
    code: "04",
    demo_profile: "ironore",
    name: "Energy & Resources · Conveyors · Fixed Plant",
    mvp: "OPC UA + OSIsoft PI native. Conveyor idler-bearing temperature drift, belt-tension anomaly, transfer-chute wear curves. AG/SAG mill liner-wear forecasting. Cross-contract benchmark variance reporting.",
    roadmap: "GE System 1 / SmartSignal / Bently Nevada direct integration for mining-customer vibration stacks.",
    proof: "One conveyor downtime hour at a Pilbara iron-ore site = $500k-$2M in lost throughput. Ventia eats some of that under contract.",
  },
  {
    Icon: Shield,
    code: "05",
    demo_profile: "defence",
    name: "Defence · Estate · Base Infrastructure",
    mvp: "Sovereign workloads (AWS Sydney / Azure Australia Central). IRAP-aligned gap analysis pre-baked. BMS + EMS read-only ingest for base utilities — power, HVAC, water.",
    roadmap: "Full IRAP PROTECTED accreditation: 12-18 month roadmap. Honest scope: non-classified base infrastructure today, not weapons or operational data.",
    proof: "Multi-year play — position as reference customer for Defence renewal cycle once IRAP-ready.",
  },
  {
    Icon: Building2,
    code: "06",
    demo_profile: "social",
    name: "Social Infrastructure · Hospitals · Schools · Public Estate",
    mvp: "Tridium Niagara / Honeywell EBI / Siemens Desigo BMS read-only ingest. Chiller-efficiency drift, AHU fault prediction, boiler combustion-curve shifts. Cleaning + security workforce optimisation against foot-traffic data.",
    roadmap: "NMI-level energy-cost optimisation vs NABERS / Greenstar targets. Predictive school-asset condition modelling.",
    proof: "5-10% reduction in reactive HVAC truck rolls across a hospital estate = significant FTE-hour saving over a 5-year contract.",
  },
  {
    Icon: Leaf,
    code: "07",
    demo_profile: "environmental",
    name: "Environmental · PFAS · Bushfire · Flood · Remediation",
    mvp: "Sensor-network ingest for soil moisture, groundwater monitoring, weather stations, flood gauges. Multi-source anomaly cross-reference: bushfire risk × asset criticality × workforce location. Regulator-grade audit trail with full data lineage.",
    roadmap: "Direct BoM + state-emergency-services feed integration. Carbon-accounting module for Ventia's Scope 1/2/3 (often a contract bonus condition).",
    proof: "One missed PFAS / EPA reporting deadline = $50-500k fine + reputational damage on next government tender.",
  },
];

const DEAL_MODELS = [
  {
    code: "A",
    name: "Per-contract pilot",
    setup: "$0 fee · 12-week pilot on 1 nominated Ventia contract",
    success: "1 avoided incident OR ≥5% efficiency gain on that contract",
    risk: "Near-zero. Ventia exits at Week 12 with no commitment.",
    upside: "After success: $120-300k / contract / yr",
    highlight: true,
  },
  {
    code: "B",
    name: "Productivity-share",
    setup: "DARA paid as % of verified FTE-hour savings on a contract (24-month window)",
    success: "Aligned by design. Zero paid until savings are independently verified.",
    risk: "Verification + measurement effort upfront. Worth it on high-cost contracts.",
    upside: "Variable — $200k-1M+ per contract depending on baseline",
  },
  {
    code: "C",
    name: "Enterprise license",
    setup: "Flat platform fee + per-asset connector cost. Like Maximo.",
    success: "Predictable SaaS economics. Lands in procurement as a standard vendor.",
    risk: "Standard SaaS risk — Ventia carries adoption.",
    upside: "$800k-2M / yr platform license · multi-contract coverage",
  },
];

const HONEST_LIMITS = [
  { Icon: AlertTriangle, label: "IRAP PROTECTED is a 12-18 month roadmap — not today. Defence scope is non-classified base infrastructure for the foreseeable.", colour: "text-[#FFB000]" },
  { Icon: AlertTriangle, label: "Real-world OT integration is 8-12 weeks minimum because of asset-owner InfoSec sign-off. We move fast, your clients move slow.", colour: "text-[#FFB000]" },
  { Icon: AlertTriangle, label: "Predictive layer covers vibration, current-draw, temperature, throughput. Acoustic + thermographic imagery is H2 2026.", colour: "text-[#FFB000]" },
  { Icon: CheckCircle2, label: "We do NOT replace Maximo / SAP / ServiceNow. We feed them. You keep your system-of-record.", colour: "text-[#00E676]" },
];

export default function Ventia() {
  usePageview("/ventia");
  useNoIndex();

  const [form, setForm] = useState({
    email: "", name: "", role: "", interest: "", contract: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) {
      toast.error("Work email is required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/industry-leads`, {
        email: form.email,
        name: form.name,
        company: "Ventia",
        role: form.role,
        message: [
          form.interest ? `Interest: ${form.interest}` : "",
          form.contract ? `Contract / sector of focus: ${form.contract}` : "",
          form.message ? `Notes: ${form.message}` : "",
        ].filter(Boolean).join("\n"),
        industry: "ventia",
        visitor_session: getVisitorSession(),
      });
      setSubmitted(true);
      toast.success("Received — DARA founder will reach out within one business day.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const briefUrl = `${API}/public/industrial-connector-brief`;
  const onePagerUrl = `${API}/public/overview-one-pager`;
  const memoUrl = `${API}/public/bank-partnership-memo`;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="ventia-page">
      <BespokePageRibbon prospectName="Ventia · Infrastructure Services AU/NZ" />
      <BespokeWatermark pageLabel="VENTIA" position="top" />
      {/* Top nav */}
      <nav className="sticky top-0 z-50 border-b border-zinc-900 bg-black/85 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="back-to-home">
            <span className="text-[#FFB000] font-mono text-sm">{`>_`}</span>
            <span className="font-heading font-black tracking-tight text-white text-base">DARA</span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 ml-2 hidden sm:inline">
              · VENTIA · BESPOKE PROPOSAL
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <a
              href={briefUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-download-brief"
              className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-[#FFB000] flex items-center gap-1.5"
            >
              <FileDown className="w-3.5 h-3.5" /> Connector brief
            </a>
            <a
              href="#engage"
              data-testid="nav-engage"
              className="bg-[#FFB000] text-black font-bold font-mono text-[10px] uppercase tracking-widest px-3 py-2 hover:opacity-90"
            >
              Continue the conversation
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="border-b border-zinc-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FFB000]/5 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 relative">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFB000] mb-4">
            // BESPOKE PROPOSAL · PREPARED FOR VENTIA · AU + NZ · {new Date().toLocaleString("en-AU", { month: "short", year: "numeric" }).toUpperCase()}
          </div>
          <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-400 mb-3">
            VENTIA <span className="text-[#FFB000]">×</span> DARA OS
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-5xl">
            The analytical layer that sits across <span className="text-[#FFB000]">every contract</span>, every asset.
          </h1>
          <p className="text-base sm:text-lg text-zinc-300 mt-6 max-w-3xl leading-relaxed">
            Ventia operates 400+ sites with 35,000 people across 7 sectors. 80%+ of revenue is recurring. The lever for the next contract renewal cycle is <span className="text-white font-bold">margin per contract</span>, not new revenue. DARA OS is the read-only intelligence layer that turns raw signals from your clients' SCADA, NMS, PI, and BMS systems into pre-emptive work orders — before your engineers have to notice them manually. <span className="text-white font-bold">No replacement of Maximo / SAP / ServiceNow. We feed them.</span>
          </p>

          <div className="flex flex-wrap gap-2 mt-7" data-testid="hero-meta-pills">
            {["TRANSPORT", "TELECOM", "WATER", "ENERGY", "DEFENCE", "SOCIAL INFRA", "ENVIRONMENTAL", "READ-ONLY", "AU SOVEREIGN", "DARA ENGINE"].map(p => (
              <span key={p} className="px-2.5 py-1 border border-[#FFB000]/40 text-[#FFB000] bg-[#FFB000]/5 font-mono text-[10px] uppercase tracking-widest">{p}</span>
            ))}
          </div>

          <div className="mt-10 flex items-center gap-4 flex-wrap">
            <a
              href="#engage"
              data-testid="hero-engage-cta"
              className="inline-flex items-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-widest px-6 py-3 hover:opacity-90"
            >
              <ChevronRight className="w-4 h-4" /> Schedule the follow-up <ArrowRight className="w-4 h-4" />
            </a>
            <Link
              to="/ventia/demo"
              data-testid="hero-live-demo-cta"
              className="inline-flex items-center gap-2 border border-[#FFB000] text-[#FFB000] font-bold font-mono text-xs uppercase tracking-widest px-6 py-3 hover:bg-[#FFB000]/10"
            >
              <Eye className="w-4 h-4" /> Watch the live 7-sector demo
            </Link>
            <a
              href="#sectors"
              data-testid="hero-sectors-link"
              className="font-mono text-[11px] uppercase tracking-widest text-zinc-300 hover:text-[#FFB000] underline-offset-4 hover:underline flex items-center gap-1.5"
            >
              <Workflow className="w-3.5 h-3.5" /> Skip to the 7-sector mapping
            </a>
          </div>
        </div>
      </section>

      {/* The wedge — one paragraph framing */}
      <section className="border-b border-zinc-900 bg-[#FFB000]/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">// THE WEDGE · IN ONE SENTENCE</div>
          <p className="text-xl sm:text-2xl font-heading font-bold leading-snug max-w-5xl tracking-tight">
            <span className="text-[#FFB000]">We become</span> the analytical layer that sits across every Ventia contract and every asset — so Ventia walks into the next renewal cycle with a <span className="text-[#FFB000]">15-20% margin uplift story</span> verified in your own numbers, not vendor slideware.
          </p>
        </div>
      </section>

      {/* Phase 25.13an — Executive video ad. Sits between the wedge and
          the architecture so a Ventia leader gets the founder pitch
          before the technical detail. 30s, muted by default. */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 border-b border-zinc-900" data-testid="ventia-video-section">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFB000] mb-3">// 30 SECONDS · FROM THE DARA FOUNDER · TO VENTIA LEADERSHIP</div>
        <div>
          <VideoAd
            src={VIDEO_EXEC}
            label="// FOUNDER · 30s · WHY US · WHY NOW"
            caption="// 14-DAY FREE TRIAL · NO CARD REQUIRED · BESPOKE PROPOSAL READY ABOVE"
            testId="ventia-video"
          />
        </div>
      </section>

      {/* Architecture diagram */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 border-b border-zinc-900">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">// WHERE DARA SITS IN VENTIA'S STACK</div>
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight max-w-3xl">
          Between your clients' asset data and your work-management system. <span className="text-[#FFB000]">Replacing nothing.</span>
        </h2>

        <div className="mt-10 space-y-4" data-testid="architecture-diagram">
          {/* Layer 1 — Client assets */}
          <div className="border border-zinc-800 bg-zinc-950/40 p-5">
            <div className="flex items-center gap-3 mb-1.5">
              <Database className="w-4 h-4 text-zinc-500" />
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">// LAYER 1 · CLIENT-OWNED ASSETS</div>
            </div>
            <div className="font-heading font-bold text-base tracking-tight">PLCs · SCADA · BMS · NMS · field sensors</div>
            <p className="text-xs text-zinc-400 mt-1.5 leading-relaxed">
              Owned by NBN, Chorus, Transpower, TMR, state water utilities, mining customers, Defence. Ventia operates but does not own. <span className="text-zinc-500">Per-client InfoSec scope.</span>
            </p>
          </div>
          {/* Arrow */}
          <div className="flex items-center justify-center gap-2 py-1">
            <Lock className="w-3 h-3 text-[#FFB000]" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000]">↓ READ-ONLY · OPC UA · OSIsoft PI · MQTT · NMS B2B API ↓</span>
          </div>
          {/* Layer 2 — DARA */}
          <div className="border border-[#FFB000]/50 bg-[#FFB000]/5 p-5">
            <div className="flex items-center gap-3 mb-1.5">
              <Cpu className="w-4 h-4 text-[#FFB000]" />
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000]">// LAYER 2 · DARA OS · THE ANALYTICAL LAYER</div>
            </div>
            <div className="font-heading font-bold text-base tracking-tight text-[#FFB000]">VENTIA × DARA</div>
            <ul className="text-xs text-zinc-300 mt-2 space-y-1.5 leading-relaxed">
              <li className="flex items-start gap-2"><Eye className="w-3 h-3 text-[#FFB000] mt-0.5 flex-shrink-0" /> Bloomberg-terminal dashboard per Ventia operations leader · live</li>
              <li className="flex items-start gap-2"><BarChart3 className="w-3 h-3 text-[#FFB000] mt-0.5 flex-shrink-0" /> DARA-grounded weekly operational brief per asset class · live</li>
              <li className="flex items-start gap-2"><AlertTriangle className="w-3 h-3 text-[#FFB000] mt-0.5 flex-shrink-0" /> Real-time anomaly / threshold-breach alerts · Slack + email · live</li>
              <li className="flex items-start gap-2"><Workflow className="w-3 h-3 text-[#FFB000] mt-0.5 flex-shrink-0" /> Cross-contract benchmarking + variance reporting · live</li>
              <li className="flex items-start gap-2"><Lock className="w-3 h-3 text-[#FFB000] mt-0.5 flex-shrink-0" /> Reproducible audit trail with full data lineage · for regulator / client reviews · live</li>
            </ul>
          </div>
          {/* Arrow */}
          <div className="flex items-center justify-center gap-2 py-1">
            <Network className="w-3 h-3 text-[#00E676]" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#00E676]">↓ PUSH SIGNALS INTO VENTIA'S EXISTING STACK ↓</span>
          </div>
          {/* Layer 3 — Ventia's existing stack */}
          <div className="border border-zinc-800 bg-zinc-950/40 p-5">
            <div className="flex items-center gap-3 mb-1.5">
              <Database className="w-4 h-4 text-zinc-500" />
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">// LAYER 3 · VENTIA WORK-MANAGEMENT STACK</div>
            </div>
            <div className="font-heading font-bold text-base tracking-tight">IBM Maximo · SAP · ServiceNow</div>
            <p className="text-xs text-zinc-400 mt-1.5 leading-relaxed">
              <span className="text-[#00E676] font-bold">Work orders auto-created.</span> Engineers receive pre-prioritised tickets. System-of-record unchanged. No procurement-rip-out required.
            </p>
          </div>
        </div>
      </section>

      {/* 7 sectors */}
      <section id="sectors" className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 border-b border-zinc-900">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">// 7-SECTOR MAPPING · WHAT DARA SHIPS TODAY · WHAT'S ON ROADMAP</div>
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight max-w-3xl">
          One platform. Seven Ventia service lines. <span className="text-[#FFB000]">Honest about MVP vs roadmap.</span>
        </h2>
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900" data-testid="sectors-grid">
          {SECTORS.map(({ Icon, code, demo_profile, name, mvp, roadmap, proof }) => (
            <div key={code} className="bg-black p-6 sm:p-7 flex flex-col" data-testid={`sector-${code}`}>
              <div className="flex items-center gap-3 mb-3">
                <Icon className="w-5 h-5 text-[#FFB000]" />
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">// SECTOR {code}</span>
              </div>
              <h3 className="font-heading font-bold text-lg tracking-tight leading-tight">{name}</h3>
              <div className="mt-4 space-y-3 flex-1">
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-[#00E676] mb-1">▸ MVP · LIVE TODAY</div>
                  <p className="text-xs text-zinc-300 leading-relaxed">{mvp}</p>
                </div>
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-[#FFB000] mb-1">▸ ROADMAP · 3-6 MONTHS</div>
                  <p className="text-xs text-zinc-400 leading-relaxed">{roadmap}</p>
                </div>
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">▸ BUSINESS CASE</div>
                  <p className="text-xs text-zinc-400 leading-relaxed italic">{proof}</p>
                </div>
              </div>
              {demo_profile && (
                <Link
                  to={`/industrial-demo?profile=${demo_profile}`}
                  data-testid={`sector-${code}-demo-link`}
                  className="mt-5 inline-flex items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-[#FFB000] border-t border-zinc-900 pt-3 group"
                >
                  <span className="flex items-center gap-1.5">
                    <Eye className="w-3 h-3" /> Watch sector {code} live · simulated tier-1 site
                  </span>
                  <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Three commercial models */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 border-b border-zinc-900">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">// COMMERCIAL · PICK YOUR DEAL STRUCTURE</div>
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight max-w-3xl">
          Three models. <span className="text-[#FFB000]">Start with A.</span>
        </h2>
        <p className="text-sm text-zinc-400 mt-3 max-w-3xl leading-relaxed">
          We deliberately don't quote you a flat price on the first conversation. We quote a structure. You pick the risk-revenue position that fits Ventia's operating envelope. Most prospects start with Model A and graduate to B or C only after success on the first pilot.
        </p>
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900" data-testid="deal-models">
          {DEAL_MODELS.map(({ code, name, setup, success, risk, upside, highlight }) => (
            <div key={code} className={`p-6 sm:p-7 ${highlight ? "bg-[#FFB000]/5" : "bg-black"}`} data-testid={`deal-model-${code}`}>
              <div className="flex items-baseline gap-3 mb-3">
                <span className={`font-heading font-black text-4xl tracking-tighter ${highlight ? "text-[#FFB000]" : "text-zinc-700"}`}>{code}</span>
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">MODEL</div>
                {highlight && <span className="ml-auto font-mono text-[9px] uppercase tracking-widest text-[#FFB000] border border-[#FFB000]/40 bg-[#FFB000]/10 px-1.5 py-0.5">RECOMMENDED START</span>}
              </div>
              <h3 className="font-heading font-bold text-lg tracking-tight">{name}</h3>
              <div className="mt-4 space-y-3 text-xs leading-relaxed">
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-0.5">SETUP</div>
                  <div className="text-zinc-300">{setup}</div>
                </div>
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-0.5">SUCCESS BAR</div>
                  <div className="text-zinc-300">{success}</div>
                </div>
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-0.5">VENTIA RISK</div>
                  <div className="text-zinc-300">{risk}</div>
                </div>
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-[#FFB000] mb-0.5">DARA UPSIDE</div>
                  <div className="text-[#FFB000] font-bold">{upside}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Honest limits */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 border-b border-zinc-900">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">// FOUR THINGS WE WILL NOT OVERSELL</div>
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight max-w-3xl">
          Honest about today vs. roadmap. <span className="text-[#FFB000]">Audit-defensible from day one.</span>
        </h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900" data-testid="honest-limits">
          {HONEST_LIMITS.map(({ Icon, label, colour }, i) => (
            <div key={i} className="bg-black p-6 flex items-start gap-3">
              <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${colour}`} />
              <p className="text-sm text-zinc-300 leading-relaxed">{label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Engage form */}
      <section id="engage" className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 border-b border-zinc-900">
        <div className="border border-[#FFB000]/40 bg-[#FFB000]/[0.03] p-6 sm:p-10">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFB000] mb-3">// CONTINUE THE CONVERSATION</div>
          <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight max-w-3xl">
            Pick one contract. <span className="text-[#FFB000]">12 weeks. $0 fee.</span>
          </h2>
          <p className="text-sm text-zinc-300 mt-3 max-w-3xl leading-relaxed">
            Submit details. DARA's founder will reach out within one business day with three proposed contracts inside Ventia's portfolio where we can deliver a measurable result in 90 days, and a draft mutual NDA for the first pilot. No procurement engagement until both sides see signal.
          </p>

          {!submitted && (
            <form onSubmit={submit} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="ventia-engage-form">
              <Input label="Work email *" testid="ventia-email" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
              <Input label="Full name" testid="ventia-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <Input label="Role / title" testid="ventia-role" value={form.role} onChange={(v) => setForm({ ...form, role: v })} />
              <div>
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Sector of immediate interest</span>
                  <select
                    value={form.interest}
                    onChange={(e) => setForm({ ...form, interest: e.target.value })}
                    data-testid="ventia-interest"
                    className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
                  >
                    <option value="">— Pick one —</option>
                    <option value="transport">Transport · Roads · Tunnels · Motorways</option>
                    <option value="telecommunications">Telecommunications · Chorus / NBN / Transpower</option>
                    <option value="water">Water utilities</option>
                    <option value="energy-resources">Energy + Resources · Mining-services</option>
                    <option value="defence">Defence · Estate / Base infrastructure</option>
                    <option value="social-infrastructure">Social infrastructure · Hospitals / Schools</option>
                    <option value="environmental">Environmental services · PFAS / Bushfire / Flood</option>
                    <option value="cross-sector">Cross-sector — start at the platform level</option>
                  </select>
                </label>
              </div>
              <div className="sm:col-span-2">
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Nominate the pilot contract (optional)</span>
                  <input
                    type="text"
                    value={form.contract}
                    onChange={(e) => setForm({ ...form, contract: e.target.value })}
                    data-testid="ventia-contract"
                    placeholder="e.g. M5 East tunnel O&M · NBN field-services region X · Water Corp WA reticulation"
                    className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
                  />
                </label>
              </div>
              <div className="sm:col-span-2">
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Anything specific you want flagged before the next call (optional)</span>
                  <textarea
                    rows={3}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    data-testid="ventia-message"
                    className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
                  />
                </label>
              </div>
              <div className="sm:col-span-2 flex items-center justify-between flex-wrap gap-3 pt-2">
                <p className="font-mono text-[10px] text-zinc-600 max-w-md">
                  By submitting you accept that DARA will contact you about this opportunity. Mutual NDA available pre-pilot.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="ventia-submit"
                  className="inline-flex items-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-widest px-6 py-3 hover:opacity-90 disabled:opacity-40"
                >
                  {submitting ? <><Sparkles className="w-3.5 h-3.5 animate-pulse" /> SENDING…</> : <><ChevronRight className="w-3.5 h-3.5" /> Send to DARA founder</>}
                </button>
              </div>
            </form>
          )}

          {submitted && (
            <div className="mt-8 border border-[#00E676]/40 bg-[#00E676]/5 p-6" data-testid="ventia-success">
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#00E676] mb-2">
                <CheckCircle2 className="w-3.5 h-3.5" />
                REQUEST RECEIVED
              </div>
              <h3 className="font-heading font-bold text-2xl tracking-tight">You're on the list.</h3>
              <p className="text-sm text-zinc-300 mt-2 max-w-xl leading-relaxed">
                DARA's founder will reach out within one business day. In the meantime, share the <a href={briefUrl} target="_blank" rel="noopener noreferrer" className="text-[#FFB000] underline">1-page connector brief</a> with your Asset / InfoSec lead.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-900 bg-zinc-950/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-2 sm:grid-cols-4 gap-6">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Documents</div>
            <ul className="space-y-2">
              <li><a href={briefUrl} target="_blank" rel="noopener noreferrer" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">Industrial Connector Brief</a></li>
              <li><a href={onePagerUrl} target="_blank" rel="noopener noreferrer" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">DARA 1-Pager</a></li>
              <li><a href={memoUrl} target="_blank" rel="noopener noreferrer" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">Bank Partnership Memo</a></li>
            </ul>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Related sectors</div>
            <ul className="space-y-2">
              <li><Link to="/industries/water" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">Water</Link></li>
              <li><Link to="/industries/mining" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">Mining</Link></li>
              <li><Link to="/industrial/connector" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">Industrial Connector</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Trust</div>
            <ul className="space-y-2">
              <li><Link to="/security" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">Security & Trust</Link></li>
              <li><Link to="/founders-guide" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">Founder's Guide</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">DARA OS</div>
            <p className="text-[11px] text-zinc-600 leading-relaxed">
              The Bloomberg-terminal for enterprise operators. Built in Australia, deployed worldwide.
            </p>
            <p className="text-[10px] text-zinc-700 leading-relaxed mt-3 font-mono">
              Bespoke proposal · prepared for Ventia · not for redistribution outside Ventia leadership without DARA permission.
            </p>
          </div>
        </div>
        <div className="border-t border-zinc-900 px-4 sm:px-6 py-4 max-w-7xl mx-auto flex items-center justify-between">
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">© DARA OS · {new Date().getFullYear()}</span>
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-700">VENTIA × DARA · v1</span>
        </div>
      </footer>
      <BespokeWatermark pageLabel="VENTIA" position="bottom" />
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required = false, testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
      />
    </label>
  );
}
